/**
 * Small curated list of ISO 3166-1 alpha-2 country codes used by the
 * tenant organization + license forms. This is deliberately NOT the full
 * 250-entry list — we pick OECD/NATO + Türkiye + a handful of partner
 * countries so the <select> fits in a dialog without overwhelming the
 * operator. A free-text fallback ("Other…") lets operators type any
 * alpha-2 code that isn't in the list.
 */

export interface Country {
    code: string; // ISO 3166-1 alpha-2
    name: string;
    flag: string; // regional-indicator emoji
}

export const COUNTRIES: ReadonlyArray<Country> = [
    { code: "TR", name: "Türkiye",        flag: "🇹🇷" },
    { code: "US", name: "United States",  flag: "🇺🇸" },
    { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
    { code: "DE", name: "Germany",        flag: "🇩🇪" },
    { code: "FR", name: "France",         flag: "🇫🇷" },
    { code: "NL", name: "Netherlands",    flag: "🇳🇱" },
    { code: "BE", name: "Belgium",        flag: "🇧🇪" },
    { code: "IT", name: "Italy",          flag: "🇮🇹" },
    { code: "ES", name: "Spain",          flag: "🇪🇸" },
    { code: "PT", name: "Portugal",       flag: "🇵🇹" },
    { code: "IE", name: "Ireland",        flag: "🇮🇪" },
    { code: "DK", name: "Denmark",        flag: "🇩🇰" },
    { code: "NO", name: "Norway",         flag: "🇳🇴" },
    { code: "SE", name: "Sweden",         flag: "🇸🇪" },
    { code: "FI", name: "Finland",        flag: "🇫🇮" },
    { code: "IS", name: "Iceland",        flag: "🇮🇸" },
    { code: "CH", name: "Switzerland",    flag: "🇨🇭" },
    { code: "AT", name: "Austria",        flag: "🇦🇹" },
    { code: "PL", name: "Poland",         flag: "🇵🇱" },
    { code: "CZ", name: "Czechia",        flag: "🇨🇿" },
    { code: "HU", name: "Hungary",        flag: "🇭🇺" },
    { code: "RO", name: "Romania",        flag: "🇷🇴" },
    { code: "GR", name: "Greece",         flag: "🇬🇷" },
    { code: "BG", name: "Bulgaria",       flag: "🇧🇬" },
    { code: "CA", name: "Canada",         flag: "🇨🇦" },
    { code: "MX", name: "Mexico",         flag: "🇲🇽" },
    { code: "JP", name: "Japan",          flag: "🇯🇵" },
    { code: "KR", name: "South Korea",    flag: "🇰🇷" },
    { code: "AU", name: "Australia",      flag: "🇦🇺" },
    { code: "NZ", name: "New Zealand",    flag: "🇳🇿" },
    { code: "IL", name: "Israel",         flag: "🇮🇱" },
    { code: "AE", name: "UAE",            flag: "🇦🇪" },
    { code: "SA", name: "Saudi Arabia",   flag: "🇸🇦" },
    { code: "SG", name: "Singapore",      flag: "🇸🇬" },
];

const BY_CODE: Map<string, Country> = new Map(COUNTRIES.map((c) => [c.code, c]));

/**
 * Look up a country by alpha-2 code (case-insensitive). Returns undefined
 * when the code isn't in the curated list — callers should render the raw
 * code as a fallback.
 */
export function lookupCountry(code: string | null | undefined): Country | undefined {
    if (!code) return undefined;
    return BY_CODE.get(code.trim().toUpperCase());
}
